/**
 * Account Form Feature Hook
 *
 * Manages form state for adding/editing accounts.
 * Encapsulates modal state, form data, and submission logic.
 */

import { useState, useCallback } from 'react';
import { useCreateAccount, useUpdateAccount, type Account } from '@/entities/account';
import { logger } from '@/shared/lib';
import type { AccountFormData, AccountFormMode, AccountFormState } from './types';

const initialFormData: AccountFormData = {
  platform: 'INSTAGRAM',
  account_id: '',
  password: '',
};

export const useAccountForm = () => {
  const [mode, setMode] = useState<AccountFormMode>(null);
  const [formData, setFormData] = useState<AccountFormData>(initialFormData);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  const createMutation = useCreateAccount();
  const updateMutation = useUpdateAccount();

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const openAdd = useCallback(() => {
    setMode('add');
    setFormData(initialFormData);
    setSelectedAccount(null);
  }, []);

  const openEdit = useCallback((account: Account) => {
    setMode('edit');
    setSelectedAccount(account);
    setFormData({
      platform: account.platform,
      account_id: account.account_id,
      password: '', // Don't prefill password
    });
  }, []);

  const close = useCallback(() => {
    setMode(null);
    setFormData(initialFormData);
    setSelectedAccount(null);
  }, []);

  const updateFormData = useCallback((data: Partial<AccountFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  }, []);

  const submit = useCallback(async () => {
    try {
      if (mode === 'add') {
        await createMutation.mutateAsync({ body: formData });
        close();
      } else if (mode === 'edit' && selectedAccount) {
        // If password is empty, send '*****' to keep existing password
        const updateData = {
          account_id: formData.account_id,
          password: formData.password || '*****',
          platform: formData.platform,
        };

        await updateMutation.mutateAsync({
          path: { accountId: selectedAccount.id },
          body: updateData,
        });
        close();
      }
    } catch (error) {
      logger.error('Failed to save account:', error);
      throw error; // Re-throw for UI error handling if needed
    }
  }, [mode, formData, selectedAccount, createMutation, updateMutation, close]);

  const state: AccountFormState = {
    mode,
    formData,
    selectedAccount,
    isSubmitting,
    openAdd,
    openEdit,
    close,
    updateFormData,
    submit,
  };

  return state;
};
